import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, ArrowRight, Shield, FileCheck, Scale, Building2, Gavel, Briefcase } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SERVICE_MENU } from '@/lib/constants';
import { usePopup } from '@/lib/PopupContext';
import { openChat } from '@/lib/chat';

const CATEGORY_ICONS = [Shield, FileCheck, Scale, Building2, Gavel, Briefcase];

export default function Header() {
  const { openPopup } = usePopup();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaRef = useRef(null);
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  const openMega = () => {
    clearTimeout(timeoutRef.current);
    setMegaOpen(true);
  };

  const closeMega = () => {
    timeoutRef.current = setTimeout(() => setMegaOpen(false), 150);
  };

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const navLinkClass = (path) =>
    `text-sm font-bold uppercase tracking-wider transition-colors hover:text-[#002FA7] ${
      location.pathname === path ? 'text-[#002FA7]' : 'text-[#09090B]'
    }`;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="header-logo">
            <img src="/assets/img/brand/logo-dark.png" alt="AmericanTrademark Firm" className="h-8 md:h-10 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            <Link to="/" className={navLinkClass('/')} data-testid="nav-home">Home</Link>
            <Link to="/about" className={navLinkClass('/about')} data-testid="nav-about">About Us</Link>

            {/* Services Dropdown */}
            <div
              ref={triggerRef}
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={closeMega}
            >
              <button
                className={`flex items-center gap-1 text-sm font-bold uppercase tracking-wider transition-colors hover:text-[#002FA7] ${
                  location.pathname.startsWith('/services') ? 'text-[#002FA7]' : 'text-[#09090B]'
                }`}
                data-testid="nav-services-trigger"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${megaOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Mega Menu */}
              {megaOpen && (
                <div
                  ref={megaRef}
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-0 pt-3"
                  onMouseEnter={openMega}
                  onMouseLeave={closeMega}
                >
                  {/* Arrow indicator */}
                  <div className="w-3 h-3 bg-white border-l border-t border-zinc-200 rotate-45 absolute top-1.5 left-1/2 -translate-x-1/2 z-10" />
                  <div
                    className="bg-white border border-zinc-200 shadow-[0_16px_48px_rgba(0,0,0,0.10)] w-[860px] relative z-0"
                    style={{ animation: 'fade-in 0.2s ease-out, slide-in-up 0.2s ease-out' }}
                    data-testid="mega-menu-panel"
                  >
                    {/* Header */}
                    <div className="border-b border-zinc-100 px-6 py-3 flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 font-heading">Our Services</p>
                      <span className="text-xs text-zinc-300 font-body">38 specialized services</span>
                    </div>
                    {/* Grid */}
                    <div className="grid grid-cols-3 max-h-[440px] overflow-y-auto mega-scroll">
                      {SERVICE_MENU.map((cat, idx) => {
                        const Icon = CATEGORY_ICONS[idx] || Shield;
                        return (
                          <div
                            key={idx}
                            className={`p-5 ${idx >= 3 ? 'border-t border-zinc-100' : ''} ${idx % 3 !== 0 ? 'border-l border-zinc-100' : ''}`}
                          >
                            <div className="flex items-center gap-2 mb-3">
                              <Icon className="w-4 h-4 text-[#002FA7]" />
                              <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#002FA7] font-heading">
                                {cat.category}
                              </p>
                            </div>
                            <ul className="space-y-0">
                              {cat.services.map((svc) => (
                                <li key={svc.slug}>
                                  <Link
                                    to={`/services/${svc.slug}`}
                                    className="group flex items-center text-sm text-zinc-600 hover:text-[#002FA7] hover:bg-[#F4F8FF] px-2.5 py-1.5 -mx-2.5 transition-all rounded-sm"
                                    data-testid={`mega-link-${svc.slug}`}
                                  >
                                    <span className="flex-1 font-body">{svc.title}</span>
                                    <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact" className={navLinkClass('/contact')} data-testid="nav-contact">Contact Us</Link>
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => openPopup()}
              className="bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-5 py-2.5 font-bold text-xs tracking-widest uppercase"
              data-testid="header-register-btn"
            >
              Register My Trademark
            </button>
            <button
              onClick={openChat}
              className="bg-transparent text-[#09090B] hover:bg-[#F4F4F5] transition-colors px-5 py-2.5 font-bold text-xs tracking-widest uppercase border border-[#09090B]"
              data-testid="header-chat-btn"
            >
              Chat Now
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu Sheet */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="right" className="w-full sm:w-[380px] p-0">
          <SheetHeader className="p-6 border-b border-zinc-200">
            <SheetTitle>
              <img src="/assets/img/brand/logo-dark.png" alt="AmericanTrademark Firm" className="h-8 w-auto" />
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-80px)]">
            <div className="p-6 space-y-4">
              <Link
                to="/"
                className="block text-sm font-bold uppercase tracking-wider py-2"
                data-testid="mobile-nav-home"
                onClick={() => setMobileOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="block text-sm font-bold uppercase tracking-wider py-2"
                data-testid="mobile-nav-about"
                onClick={() => setMobileOpen(false)}
              >
                About Us
              </Link>

              {/* Services Accordion */}
              <Accordion type="single" collapsible>
                <AccordionItem value="services" className="border-none">
                  <AccordionTrigger className="text-sm font-bold uppercase tracking-wider py-2 hover:no-underline" data-testid="mobile-services-trigger">
                    Services
                  </AccordionTrigger>
                  <AccordionContent>
                    <Accordion type="single" collapsible>
                      {SERVICE_MENU.map((cat, idx) => (
                        <AccordionItem key={idx} value={`cat-${idx}`} className="border-none">
                          <AccordionTrigger className="text-xs font-bold uppercase tracking-wider text-[#002FA7] py-2 pl-4 hover:no-underline">
                            {cat.category}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-1 pl-8">
                              {cat.services.map((svc) => (
                                <Link
                                  key={svc.slug}
                                  to={`/services/${svc.slug}`}
                                  className="block text-sm text-zinc-600 py-1.5 hover:text-[#09090B]"
                                  onClick={() => setMobileOpen(false)}
                                >
                                  <ChevronRight className="w-3 h-3 inline mr-1" />
                                  {svc.title}
                                </Link>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <Link
                to="/contact"
                className="block text-sm font-bold uppercase tracking-wider py-2"
                data-testid="mobile-nav-contact"
                onClick={() => setMobileOpen(false)}
              >
                Contact Us
              </Link>

              <div className="pt-4 space-y-3 border-t border-zinc-200">
                <button
                  onClick={() => { setMobileOpen(false); openPopup(); }}
                  className="w-full bg-[#002FA7] text-white hover:bg-[#002480] transition-colors px-5 py-3 font-bold text-xs tracking-widest uppercase"
                  data-testid="mobile-register-btn"
                >
                  Register My Trademark
                </button>
                <button
                  onClick={() => { setMobileOpen(false); openChat(); }}
                  className="w-full bg-transparent text-[#09090B] hover:bg-[#F4F4F5] transition-colors px-5 py-3 font-bold text-xs tracking-widest uppercase border border-[#09090B]"
                  data-testid="mobile-chat-btn"
                >
                  Chat Now
                </button>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </header>
  );
}
